<template>
  <div class="page-container bg-#f6f6f6 min-h-100%">
    <!-- 搜索区域 -->
    <van-sticky>
      <van-tabs
        v-model:active="mainTabActive"
        @click-tab="onClickMainTab"
        line-width="16px"
        title-inactive-color="#999999"
        title-active-color="#222222"
      >
        <van-tab v-for="item in mainTabList" :key="item.key" :name="item.key">
          <template #title>
            <span class="text-15px">{{ item.title }}</span>
          </template>
        </van-tab>
      </van-tabs>
      <div class="bg-white py-16px">
        <SearchBar
          v-model="keyword"
          placeholder="请输入手机号码"
          :has-search-btn="true"
          :search-btn-outside="true"
          @search="onSearch"
        />
      </div>

      <!-- Tab区域 -->
      <van-tabs
        v-if="isTeamTab"
        v-model:active="tabActive"
        @click-tab="onClickTab"
        line-width="12px"
        title-inactive-color="#999999"
        title-active-color="#222222"
      >
        <van-tab v-for="item in tabList" :key="item.key" :name="item.key">
          <template #title>
            <span class="text-15px">{{ item.title }}</span>
            <span v-if="tabActive === item.key">({{ total }})</span>
          </template>
        </van-tab>
      </van-tabs>
    </van-sticky>
    <!-- 用户列表 -->
    <van-pull-refresh v-model="refreshing" @refresh="onRefresh">
      <van-list
        v-model:loading="loading"
        :finished="finished"
        :immediate-check="false"
        :finished-text="userList.length ? `共${total}条数据，已全部加载完成` : ''"
        @load="onLoad"
      >
        <div class="user-list px-12px pt-12px">
          <UserItem
            v-for="(item, index) in userList"
            :key="index"
            :data="item"
            class="mb-12px"
            bg-color="#fff"
            :padding="true"
            @click="handleUserClick(item)"
          />
        </div>

        <!-- 空状态 -->
        <van-empty
          v-if="userList.length === 0 && !loading"
          image="https://i.xurl.cn/x_dsx_h5admin/icons/empty-relations.png"
          image-size="240"
          description="暂无记录哦~"
        />
      </van-list>
    </van-pull-refresh>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { showToast, List as vanList, PullRefresh as vanPullRefresh } from 'vant'
import SearchBar from '@/components/SearchBar.vue'
import UserItem from '@/components/UserItem.vue'
import usePage from '@/hooks/usePage'
import { teamMemberList } from '@/api/team'
import { useUserStore } from '@/store/user'
import { getMyFansList } from '@/api/myFans'


const { navigateTo } = usePage()
const userStore = useUserStore()
const userInfo = computed(() => userStore.getUserInfo) //用户信息

// 搜索关键词
const keyword = ref('')
// 当前tab
const tabActive = ref(1)
// 加载状态
const loading = ref(false)
// 下拉刷新状态
const refreshing = ref(false)
// 是否加载完成
const finished = ref(false)
// 总条数（接口返回）
const apiTotal = ref(0)
// 已加载的原始数据条数
const loadedCount = ref(0)
// 分页参数
const pageInfo = ref({
  page: 1,
  pageSize: 20
})

// mainTabList列表
const mainTabList = ref([
  { key: 1, title: '我的团队' },
  { key: 2, title: '我的粉丝' }
])
// 当前mainTab
const mainTabActive = ref(1)
const isTeamTab = computed(() => mainTabActive.value === 1)
// 点击mainTab列表
const onClickMainTab = e => {
  mainTabActive.value = e.name
  keyword.value = ''
  resetList()
  getList()
}

// Tab列表
const tabList = ref([
  { key: 1, title: '直属下级' },
  { key: 2, title: '同级直推' },
  { key: 3, title: '同级间推' },
  { key: 0, title: '全部' }
])

// 原始用户列表数据
const rawUserList = ref([])
// 过滤掉自己后的用户列表
const userList = computed(() => rawUserList.value.filter(item => !item.isSelf))
// 过滤掉自己后的总数
const total = computed(() => {
  const selfCount = rawUserList.value.filter(item => item.isSelf).length
  return Math.max(0, apiTotal.value - selfCount)
})

// 重置列表
const resetList = () => {
  rawUserList.value = []
  apiTotal.value = 0
  loadedCount.value = 0
  pageInfo.value.page = 1
  loading.value = false
  refreshing.value = false
  finished.value = false
}

// 搜索
const onSearch = () => {
  resetList()
  getList()
}

// Tab切换
const onClickTab = e => {
  tabActive.value = e.name
  resetList()
  getList()
}

// 下拉刷新
const onRefresh = () => {
  resetList()
  getList()
}

// 触底加载
const onLoad = () => {
  getList()
}

// 获取列表数据
const getList = async () => {
  loading.value = true
  try {
    let result
    if (isTeamTab.value) {
      result = await teamMemberList({
        keyword: keyword.value,
        agentRelation: tabActive.value,
        page: pageInfo.value.page,
        pageSize: pageInfo.value.pageSize
      })
    } else {
      result = await getMyFansList({
        keyword: keyword.value,
        page: pageInfo.value.page,
        pageSize: pageInfo.value.pageSize
      })
    }

    // 数据映射：将接口返回字段映射为 UserItem 组件需要的字段
    const list = (result.list || []).map(item => ({
      avatarUrl: item.avatarUrl,
      nickname: item.nickname,
      level: item.agentLevel,
      levelName: item.agentLevelName,
      mobile: item.mobile,
      inviterLevelName: item.inviterLevelName || item.inviterName,
      inviterMobile: item.inviterMobile,
      puid: item.puid,
      isSelf: item.puid === userInfo.value?.puid
    }))

    rawUserList.value = [...rawUserList.value, ...list]
    apiTotal.value = result.total || 0
    loadedCount.value += list.length

    // 使用已加载的原始数据量判断是否完成
    if (loadedCount.value >= apiTotal.value) {
      finished.value = true
    } else {
      pageInfo.value.page++
    }
  } catch (e) {
    rawUserList.value = []
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

onMounted(() => {
  // 初始化加载数据
  getList()
})

// 点击用户跳转到转赠页面
const handleUserClick = item => {
  // 将用户数据编码后传递
  const userData = encodeURIComponent(JSON.stringify(item))
  navigateTo(`/ycktz?puid=${item.puid}&userData=${userData}`)
}
</script>

<style lang="less" scoped>
:deep(.van-tabs__nav) {
  background: #fff;
}
:deep(.van-tabs__wrap) {
  height: 44px;
}
:deep(.van-tab) {
  font-size: 15px;
}
:deep(.van-tabs__line) {
  bottom: 15px;
  height: 2px;
  background-color: #222;
}
</style>
